import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Col,
  Container,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { REGEX } from "components/Common/constants";
import { forgetPassword } from "actions/forgetPassword";

const initialFields = {
  email: { value: "", error: "" },
};

export const ForgetPassword = () => {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);
  const [forgotPasswordInfo, setForgotPasswordInfo] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForgotPasswordInfo((prev) => ({
      ...prev,
      [name]: {
        value: value.trim(),
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "email":
        return !value
          ? "Please enter an email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid email"
            : "";
      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);

    setForgotPasswordInfo((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...forgotPasswordInfo };

    Object.keys(newData).forEach((key) => {
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });

    setForgotPasswordInfo(newData);
    return Object.values(newData).every((item) => item.error === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setShowLoaderOnClick(true);
      const emailData = {
        email: forgotPasswordInfo.email.value,
        ClientType: "WEB",
      };

      const res = await forgetPassword(emailData);
      if (res?.code === 200) {
        notify("success", res.message || "Recovery link sent to your email");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to send recovery link";

        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Failed to send recovery link to your email");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  return (
    <>
      {/* Notification */}
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>

      {/* Wrapper */}
      <div className="forgot-wrapper">
        <Container>
          <Row className="justify-content-center">
            <Col md="6" lg="6" xl="6">
              <Card className="forgot-card">
                <CardHeader className="text-center bg-transparent border-0">
                  <CardTitle tag="h4" className="forgot-title">
                    Forgot password
                  </CardTitle>
                  <hr />
                </CardHeader>

                <CardBody>
                  <p className="text-center forgot-subtitle">
                    Enter your email to receive recovery link
                  </p>

                  <Form onSubmit={handleSubmit}>
                    <FormGroup
                      className={`mt-4 ${
                        forgotPasswordInfo.email.error ? "has-danger" : ""
                      } `}
                    >
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="tim-icons icon-email-85" />
                          </InputGroupText>
                        </InputGroupAddon>

                        <Input
                          type="email"
                          name="email"
                          placeholder="Enter your email"
                          value={forgotPasswordInfo.email.value}
                          onChange={handleChange}
                          onBlur={(e) => handleOnBlur("email", e.target.value)}
                        />
                      </InputGroup>

                      {forgotPasswordInfo.email.error && (
                        <small className="validation-error">
                          {forgotPasswordInfo.email.error}
                        </small>
                      )}
                    </FormGroup>

                    <Button
                      type="submit"
                      color="default"
                      className="w-100 forgot-btn"
                      disabled={showLoaderOnClick}
                    >
                      {showLoaderOnClick ? "Sending..." : "Send Recovery Email"}
                    </Button>

                    <div className="text-center mt-3">
                      <span
                        className="forgot-back"
                        onClick={() => navigate("/auth/login")}
                      >
                        Back
                      </span>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};
